import { IVideoCallCurrent } from '~/store/reducers/callCurrent';
import * as actionTypes from '~/store/actions/actionTypes';
import { log } from '~/helpers';

/* eslint-disable */
type IPhoneStatus = 'Unavailable' | 'Available' | 'Busy';

interface ICallState {
  callId: null | string;
  callAnswer: null | string;
  callFinish: null | string;
}

interface ICallProtocol {
  textValue: string;
  value: string;
}

interface IInComingCallInfo {
  call: {
    id: string;
    from: string;
  };
  activeCalls: number;
}

// eslint-disable-next-line no-undef
// interface IOutgoingCallInfo extends Omit<IInComingCallInfo, 'activeCalls'> {
interface IOutgoingCallInfo {
  call: {
    id: string;
    from: string;
    status: string;
  };
}

interface IConversationInfo {
  conversationId: string;
  title: string;
  body: string;
}

interface IpcRendererClient {
  storeLoadedWebVersion(version: string): void;
  hideNotification(callId: string): void;
  forceReload(): void;
  changePhoneStatus(status: IPhoneStatus, callActivities: boolean): void;
  callAnswered(callId: string, direction: string): void;
  /**
   * Update notification message. Use unformatted callId
   * @param callId 25be9d9233a8576f6abb3af564fed2ce@secure-call.nuacom.netas1cb18166
   * @param direction INCOMING Only
   * @param displayName Number or name
   */
  updateNotificationDisplayName(
    callId: string,
    direction: string,
    displayName: string
  ): void;
  callFinished(callId: string): ICallState;
  getDevtoolsState(): boolean;
  makeCallbyProtocol(): ICallProtocol | undefined;
  resetProtocol(): void;
  changeDevtoolsState(isDevtools: boolean): void;
  incomingCall(incomingCallInfo: IInComingCallInfo): void;
  outgoingCall(outgoingCallInfo: IOutgoingCallInfo): void;
  languageChanged(lng: any): void;
  openExternalUrl(url: any): void;
  downloadFile(payload: { url: string; filename?: string }): void;
  incomingUpdateAccepted(): void;
  startVideoCall(callSession: IVideoCallCurrent): void;
  endVideoCall(): void;
  log(data: string, extra?: any): void;
  displayIncomingMessage(messageInfo: IConversationInfo): void;
  requestScreenShare(): Promise<any>;
  checkScreenPermission(): Promise<string>;
  requestCameraPermission(): Promise<string>;
}

export function getElectronIpcRendererEvents(
  isElectron: any,
  ipcRenderer: any
): IpcRendererClient {
  return {
    storeLoadedWebVersion: version => {
      return (
        isElectron && ipcRenderer.send('store-loaded-web-version', { version })
      );
    },
    hideNotification: callId => {
      return (
        isElectron &&
        ipcRenderer.send('hideNotification', { call: { id: callId } })
      );
    },
    forceReload: () => {
      return isElectron && ipcRenderer.send('forceReload');
    },
    changePhoneStatus: (status, callActivities) => {
      return (
        isElectron &&
        ipcRenderer.send('StatusPhone', { status, callActivities })
      );
    },
    incomingUpdateAccepted: () => {
      return isElectron && ipcRenderer.send('incoming-update-accepted');
    },
    callAnswered: (callId, direction) => {
      return (
        isElectron &&
        ipcRenderer.send('callAnswered', {
          call: { id: callId, callDirection: direction },
        })
      );
    },
    updateNotificationDisplayName: (callId, direction, displayName) => {
      return (
        direction === actionTypes.CALL_INCOMING &&
        isElectron &&
        ipcRenderer.send('updateNotificationDisplayName', {
          call: { callId: callId, displayName: displayName },
        })
      );
    },
    callFinished: callId => {
      return (
        isElectron &&
        ipcRenderer.send('callFinished', {
          call: { id: callId },
        })
      );
    },
    getDevtoolsState: () => {
      if (isElectron) {
        return ipcRenderer.sendSync('getDevtoolsState');
      }
      return false;
    },
    makeCallbyProtocol: () => {
      if (isElectron) {
        return ipcRenderer.sendSync('makeCallbyProtocol', '');
      }
      return undefined;
    },
    resetProtocol: () => {
      return isElectron && ipcRenderer.send('resetProtocol', '');
    },
    changeDevtoolsState: isDevtools => {
      return isElectron && ipcRenderer.send('changeDevtoolsState', !isDevtools);
    },
    incomingCall: incomingCallInfo => {
      return isElectron && ipcRenderer.send('incomingCall', incomingCallInfo);
    },
    outgoingCall: outgoingCallInfo => {
      return isElectron && ipcRenderer.send('outgoingCall', outgoingCallInfo);
    },
    languageChanged: lng => {
      return isElectron && ipcRenderer.send('language-changed', lng);
    },
    openExternalUrl: url => {
      return isElectron
        ? ipcRenderer.send('openExternalUrl', url)
        : window.open(url, '_blank');
    },
    downloadFile: payload => {
      return isElectron && ipcRenderer.send('download-file', payload);
    },
    startVideoCall: callSession => {
      return isElectron && ipcRenderer.send('startVideoCall', callSession);
    },
    requestScreenShare: () => {
      return isElectron && ipcRenderer.invoke('requestScreenShare');
    },
    checkScreenPermission: () => {
      return isElectron && ipcRenderer.invoke('checkScreenPermission');
    },
    requestCameraPermission: () => {
      return isElectron && ipcRenderer.invoke('requestCameraPermission');
    },
    endVideoCall: () => {
      return isElectron && ipcRenderer.send('endVideoCall');
    },
    log: (data, extra) => {
      log.info(data, extra ? extra : '');
      if (isElectron) {
        return ipcRenderer.send('log', data);
      }
    },
    displayIncomingMessage: conversationInfo => {
      return (
        isElectron &&
        ipcRenderer.send('display-incoming-message', conversationInfo)
      );
    },
  };
}

function electronEventsFactory() {
  const { isElectron, ipcRenderer } = window;
  return getElectronIpcRendererEvents(isElectron, ipcRenderer);
}

export default electronEventsFactory();
